import { cn } from "@/lib/utils";
import useDesignSystemStore, {
  GenerationStatus,
} from "@/store/zustand/aiDesignSystemBuilder";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import {
  BlocksIcon,
  BookOpenIcon,
  ComponentIcon,
  EllipsisIcon,
  FileIcon,
  GitBranchIcon,
  HistoryIcon,
  InfoIcon,
  LayersIcon,
  Store,
  Settings,
} from "lucide-react";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import HistoryPanel from "../ActivityStream/HistoryPanel";
import { Chat } from "../AI/chat/Chat";
import { AssetsPanel } from "../AssetsPanel/AssetsPanel";
import Icon, { ICONS } from "../Core/Icons";
import DesignSystemBuilder from "../DesignSystemBuilder";
import DomTree from "../LeftNav/DomTree";
import FileSelector from "../LeftNav/FileSelector";
import GitSettings from "../LeftNav/GitSettings";
import { Button } from "../ui/button";
import SupabaseConnect from "../LeftNav/SupabaseConnect";
import LocalEditing from "../LeftNav/LocalEditing";
import InfoPanel from "../LeftNav/Info";
import { track } from "@/utils/events/tracker";
import Convex from "../LeftNav/Convex";
import AppStore from "../LeftNav/AppStore";
import ProjectSettings from "../settings";
import CommunityPanel from "../Community/CommunityPanel";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useOpenDeepLinkForEditor } from "@/utils/localEditingUtils";
import { setShowAddEditProjectModal } from "@/store/projectsSlice";

const AiLogoIcon = (props: any) => (
  <Icon
    icon={ICONS.AI_LOGO}
    width={20}
    height={20}
    {...props}
    className={cn(
      "cursor-pointer text-white p-1 w-auto h-6 rounded-sm bg-primary-0 hover:bg-primary-100",
      props.selected && "bg-primary-100"
    )}
  />
);

const SupabaseLogoIcon = (props: any) => (
  <img src="/images/supabase-logo-icon.svg" alt="Supabase" {...props} />
);

const LocalEditorIcon = (props: any) => {
  const icon = <img src="/images/vscode-icon.svg" alt="VSCode" {...props} />;

  return (
    <div className="relative">
      <div className={"transition-opacity duration-200 opacity-100"}>
        {icon}
      </div>
    </div>
  );
};

const ConvexLogoIcon = (props: any) => (
  <img src="/images/convex.png" alt="Convex" {...props} />
);

interface TabContentProps {
  activeTab: LeftTab;
  tab: LeftTab | LeftTab[];
  children: React.ReactNode;
}

const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  tab,
  children,
}) => {
  const isActive = Array.isArray(tab)
    ? tab.includes(activeTab)
    : tab === activeTab;

  return (
    <div
      className={
        isActive ? "flex-grow h-full hide-scrollbar min-w-0" : "hidden"
      }
    >
      {children}
    </div>
  );
};

const Tabs = React.memo(() => {
  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);
  const setEditorContext = useCodebaseStore((state) => state.setEditorContext);

  const leftTab = useLayoutStore((state) => state.leftTab);
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const status = useDesignSystemStore((state) => state.generation.status);

  const conditions = useCodebaseStore((state) => state.conditions);

  // Check if the project is using a Convex template
  const isConvexTemplate = Boolean(conditions["convexInstalled"]?.met);

  const allowCommunity = useFeatureFlagEnabled("community");

  const openDeepLinkForEditor = useOpenDeepLinkForEditor();

  const icons: any = [
    {
      tab: LeftTab.AIChat,
      IconEl: AiLogoIcon,
      alt: "AI Icon",
      tooltip: "AI Chat",
      selected: leftTab === LeftTab.AIChat,
    },
    "SEPARATOR",
    {
      tab: LeftTab.DomTree,
      IconEl: LayersIcon,
      alt: "Dom Nav Icon",
      tooltip: "Dom Tree",
      selected: leftTab === LeftTab.DomTree,
    },
    {
      tab: LeftTab.Assets,
      IconEl: ComponentIcon,
      alt: "Assets Icon",
      tooltip: "Assets",
      selected: leftTab === LeftTab.Assets,
    },
    ...(allowCommunity
      ? [
          {
            tab: LeftTab.Community,
            IconEl: BookOpenIcon,
            alt: "Community Icon",
            tooltip: "Community",
            selected: leftTab === LeftTab.Community,
          },
        ]
      : []),
    "SEPARATOR",
    {
      tab: LeftTab.FileSelector,
      IconEl: FileIcon,
      alt: "File Selector Icon",
      tooltip: "File Selector",
      selected: leftTab === LeftTab.FileSelector,
    },
    {
      tab: LeftTab.Git,
      IconEl: GitBranchIcon,
      alt: "Git Settings Icon",
      tooltip: "Git Settings",
      selected: leftTab === LeftTab.Git,
    },
    {
      tab: LeftTab.History,
      IconEl: HistoryIcon,
      alt: "History Icon",
      tooltip: "History",
      selected: leftTab === LeftTab.History,
    },
    {
      tab: LeftTab.DesignSystemBuilder,
      IconEl: BlocksIcon,
      alt: "Design System Icon",
      tooltip: "Design System",
      selected: leftTab === LeftTab.DesignSystemBuilder,
    },
    {
      tab: LeftTab.AppStore,
      IconEl: Store,
      alt: "App Store",
      tooltip: "App Store",
      selected: leftTab === LeftTab.AppStore,
    },
    ...(isConvexTemplate
      ? [
          {
            tab: LeftTab.Convex,
            IconEl: ConvexLogoIcon,
            alt: "Convex Icon",
            tooltip: "Convex",
            selected: leftTab === LeftTab.Convex,
          },
        ]
      : []),
    // Only show Supabase connection tab if not using a Convex template
    ...(!isConvexTemplate
      ? [
          {
            tab: LeftTab.SupabaseConnect,
            IconEl: SupabaseLogoIcon,
            alt: "Connect Supabase",
            tooltip: "Connect Supabase",
            selected: leftTab === LeftTab.SupabaseConnect,
          },
        ]
      : []),
    {
      tab: LeftTab.FigmaExtension,
      IconEl: (props) => (
        <img src="/images/figma-logo.svg" alt="Figma" {...props} />
      ),
      alt: "Figma Extension",
      tooltip: "Figma Extension",
    },
    {
      tab: LeftTab.LocalEditing,
      IconEl: LocalEditorIcon,
      alt: "Local Editing",
      tooltip: "Local Editing",
      selected: leftTab === LeftTab.LocalEditing,
    },
    {
      tab: LeftTab.Info,
      IconEl: InfoIcon,
      alt: "Info Icon",
      tooltip: "Info",
      selected: leftTab === LeftTab.Info,
    },
    {
      tab: LeftTab.ProjectSettings,
      IconEl: Settings,
      alt: "Project settings Icon",
      tooltip: "Project settings",
      selected: leftTab === LeftTab.ProjectSettings,
    },
  ];

  useEffect(() => {
    if (leftTab === LeftTab.EngSpec) {
      setEditorContext(EditorContext.EngSpec);
    } else if (leftTab === LeftTab.History) {
      setEditorContext(EditorContext.History);
    } else if (leftTab === LeftTab.DesignSystemBuilder) {
      if (status >= GenerationStatus.GeneratingStory) {
        setEditorContext(EditorContext.StoryGeneration);
      } else {
        setEditorContext(EditorContext.Code);
      }
    } else {
      setEditorContext(EditorContext.Code);
    }
  }, [leftTab, status]);

  const uncommittedChangedFiles =
    gitStatus?.status?.files?.map((file) => file.path) || [];

  return (
    <aside className="shrink-0 w-12 flex overflow-hidden flex-col align-center justify-between min-h-0 border-r">
      <ul className="items-center flex flex-col gap-1 flex-grow-0 pt-2">
        {icons.map((icon, index) => {
          if (icon === "SEPARATOR") {
            return (
              <div
                className="bg-secondary-300 h-[2px] w-[calc(100%-10px)] my-1 block"
                key={`separator-${index}`}
              />
            );
          }

          const IconEl = icon.IconEl;

          const onClickFn = () => {
            // prevent switching to null tab when opening figma
            if (icon.tab == LeftTab.FigmaExtension) {
              track("FIGMA_LINK");
              window.open(
                "https://www.figma.com/community/plugin/1463689183126672406/tempo-labs",
                "_blank"
              );
              return;
            }

            if (leftTab !== icon.tab) {
              // @ts-expect-error TS2345
              setNewLayout(icon.tab, null);
            }
          };

          return (
            <li key={icon.tab}>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "p-0 relative items-center text-icons-muted justify-center h-8 w-8 rounded-lg",
                  icon.selected ? "bg-accent text-accent-foreground" : undefined
                )}
                onClick={onClickFn}
              >
                <IconEl
                  data-tooltip-id="tooltip"
                  data-tooltip-place="right"
                  data-tooltip-content={icon.tooltip}
                  className="h-5 w-5 outline-none"
                />
                {icon.tab === LeftTab.Git && uncommittedChangedFiles?.length ? (
                  <div className="absolute w-4 h-4 text-3xs bg-primary-0 flex justify-center items-center rounded-sm text-center text-foreground top-4 left-3">
                    {uncommittedChangedFiles.length}
                  </div>
                ) : null}
                {icon.tab === LeftTab.DesignSystemBuilder &&
                status > GenerationStatus.Pending &&
                status < GenerationStatus.Success ? (
                  <div className="w-[18px] h-[18px] bg-primary-0 flex justify-center items-center rounded-full absolute text-white fill-primary-0 left-[45.83%] right-[45.83%] top-[45.83%] bottom-[16.67%] text-primary-0">
                    <EllipsisIcon className="text-white w-3 h-3" />
                  </div>
                ) : null}
              </Button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
});

const LeftPanel = React.memo(() => {
  const leftTab = useLayoutStore((state) => state.leftTab);
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const allowCommunity = useFeatureFlagEnabled("community");

  const showAddEditProjectModal = useSelector(
    (state: any) => state.projects.showAddEditProjectModal
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (showAddEditProjectModal) {
      setNewLayout(LeftTab.ProjectSettings, undefined);
      dispatch(setShowAddEditProjectModal(false));
    }
  }, [showAddEditProjectModal, setNewLayout, dispatch]);

  return (
    <div className="relative h-full w-full flex flex-row z-[1001]">
      <Tabs />
      {/* Tab Content */}
      <TabContent activeTab={leftTab} tab={[LeftTab.AIChat, LeftTab.EngSpec]}>
        <Chat />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.DomTree}>
        <DomTree />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Assets}>
        <AssetsPanel />
      </TabContent>
      {allowCommunity && (
        <TabContent activeTab={leftTab} tab={LeftTab.Community}>
          <CommunityPanel isActiveTab={leftTab === LeftTab.Community} />
        </TabContent>
      )}
      <TabContent activeTab={leftTab} tab={LeftTab.FileSelector}>
        <FileSelector />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Git}>
        <GitSettings />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.History}>
        <HistoryPanel />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.DesignSystemBuilder}>
        <DesignSystemBuilder />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.ProjectSettings}>
        <ProjectSettings />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.SupabaseConnect}>
        <SupabaseConnect />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.LocalEditing}>
        <LocalEditing />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Info}>
        <InfoPanel />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Convex}>
        <Convex />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.AppStore}>
        <AppStore />
      </TabContent>
    </div>
  );
});

export default LeftPanel;
